<script>
export default {
	name: 'SubTabHistory',
};
</script>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';

const selectedFlypassData = ref(null);
const flypassHistoryData = ref(null);
const isLoadingHistory = ref(false);
const historyError = ref(null);
const noResultImage = ref('');
const expandedItems = ref(new Set());
const flypassOriDestData = ref(null);

const props = defineProps({
	tripDetailsCtaLabel: { type: String, default: '' },
	pnrLabel: { type: String, default: '' },
	eTicketNumberLabel: { type: String, default: '' },
	tripTypeLabel: { type: String, default: '' },
	bookingDateLabel: { type: String, default: '' },
	statusLabel: { type: String, default: '' },
	creditRedeemedLabel: { type: String, default: '' },
	noResults: { type: Object, default: () => ({
		imageUrl: { type: String, default: '' },
		title: { type: String, default: '' },
		message: { type: String, default: '' },
	}) },
});

// Computed property to get history items
const historyItems = computed(() => {
	return flypassHistoryData.value?.enrichFlypassHistoryInfo || [];
});

const toggleTripDetails = (itemIndex) => {
	if (expandedItems.value.has(itemIndex)) {
		expandedItems.value.delete(itemIndex);
	} else {
		expandedItems.value.add(itemIndex);
	}
};

const isItemExpanded = (itemIndex) => {
	return expandedItems.value.has(itemIndex);
};

// Format date helper
const formatDate = (dateString) => {
	if (!dateString) return '';
	return dayjs(dateString).format('DD MMM YYYY');
};

// Format time helper
const formatTime = (dateString) => {
	if (!dateString) return '';
	return dayjs(dateString).format('HH:mm');
};

// Get trip type display
const getTripTypeDisplay = (tripType) => {
	switch (tripType) {
		case 'R': return 'Return';
		case 'O': return 'One-way';
		default: return tripType;
	}
};

const loadFlypassData = async () => {
	// First check for selectedFlypassData (from View History button)
	let storedData = sessionStorage.getItem('selectedFlypassData');
	let flypassData = null;
	
	if (storedData) {
		flypassData = JSON.parse(storedData);
		flypassOriDestData.value = flypassData.flypassData;
		sessionStorage.removeItem('selectedFlypassData');
	} else {
		// If no selectedFlypassData, check for redeemFlypassData (from Redeem button)
		const redeemData = sessionStorage.getItem('redeemFlypassData');
		if (redeemData) {
			const parsedRedeemData = JSON.parse(redeemData);
			// Get API URLs from sessionStorage (set by fetchActiveFlyPass)
			const flypassApi = sessionStorage.getItem('flypassApi');
			if (flypassApi) {
				const apiData = JSON.parse(flypassApi);
				flypassData = {
					id: parsedRedeemData.id,
					historyApi: apiData.historyApi,
				};
				flypassOriDestData.value = parsedRedeemData.flypassData;
			}
		}
	}
	
	if (flypassData) {
		selectedFlypassData.value = flypassData;
		
		// Call API with the flypass ID using the historyApi
		if (flypassData.id && flypassData.historyApi) {
			await fetchFlypassHistory(flypassData.id, flypassData.historyApi);
		}
	}
};

const fetchFlypassHistory = async (flypassId, apiUrl) => {
	isLoadingHistory.value = true;
	historyError.value = null;
	
	try {
		// Make API call with flypass ID as parameter
		const response = await axios.get(apiUrl, {
			params: {
				travelPass: flypassId,
			},
		});
		
		flypassHistoryData.value = response.data;
		
	} catch (error) {
		console.error('Error fetching flypass history:', error);
		historyError.value = error.message || 'Failed to load flypass history';
	} finally {
		isLoadingHistory.value = false;
	}
};

const getAirportInfoByCode = (airportCode) => {
	if (!flypassOriDestData.value || !airportCode) return null;
	
	// Search in originList first
	if (flypassOriDestData.value.originList) {
		const originAirport = flypassOriDestData.value.originList.find((airport) => airport.airportCode === airportCode);
		if (originAirport) {
			return originAirport.airportName;
		}
	}
	
	// Search in destinationList if not found in originList
	if (flypassOriDestData.value.destinationList) {
		const destinationAirport = flypassOriDestData.value.destinationList.find((airport) => airport.airportCode === airportCode);
		if (destinationAirport) {
			return destinationAirport.airportName;
		}
	}
	
	// Return null if no match found
	return null;
};

onMounted(() => {
	loadFlypassData();
});

</script>

<template>
<div class="SubTabHistory">
	<!-- History Data -->
	<div v-if="historyItems.length > 0" class="space-y-5">
		<!-- History Items -->
		<div
			v-for="(item, index) in historyItems"
			:key="item.id || index"
			class="subtab-history-item"
		>
			<div class="flex">
				<div class="subtab-history-item-depart flex-1">
					<div class="flight-info-grid">
						<div class="date-slot">{{ item.departDate ? formatDate(item.departDate) : '' }}</div>
						<div class="airport-code-slot">
							<div class="airport-code-tag">{{ item.origin }}</div>
						</div>
						<div class="description-slot">{{ getAirportInfoByCode(item.origin) || '' }}</div>
					</div>
				</div>
				<div class="flex justify-center items-center px-5">
					<icon-fas-plane class="w-4 h-auto fill-primary-blue-base" />
				</div>
				<div class="subtab-history-item-return flex-1">
					<div class="flight-info-grid">
						<div class="date-slot">{{ item.returnDate ? formatDate(item.returnDate) : '' }}</div>
						<div class="airport-code-slot">
							<div class="airport-code-tag">{{ item.destination }}</div>
						</div>
						<div class="description-slot">{{ getAirportInfoByCode(item.destination) || '' }}</div>
					</div>
				</div>
			</div>
			<div class="flex items-center mt-2.5 gap-1 cursor-pointer" @click="toggleTripDetails(index)">
				<span class="text-sm font-semibold text-primary-blue-base">{{ props.tripDetailsCtaLabel }}</span>
				<icon-fal-chevron-down
					class="icon-arrow transform"
					:class="{ 'rotate-180': isItemExpanded(index) }"
				/>
			</div>
			<div v-show="isItemExpanded(index)" class="subtab-history-item-details flex flex-wrap mt-4 gap-6">
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.pnrLabel }}</p>
					<p>{{ item.pnr }}</p>
				</div>
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.eTicketNumberLabel }}</p>
					<p>{{ item.ticketNumber }}</p>
				</div>
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.tripTypeLabel }}</p>
					<p>{{ getTripTypeDisplay(item.tripType) }}</p>
				</div>
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.bookingDateLabel }}</p>
					<p>{{ formatDate(item.bookingDate) }}</p>
				</div>
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.statusLabel }}</p>
					<p>{{ item.status?.charAt(0).toUpperCase() + item.status?.slice(1).toLowerCase() }}</p>
				</div>
				<div>
					<p class="text-sm text-neutral-grey-extradark font-semibold mb-1">{{ props.creditRedeemedLabel }}</p>
					<p>{{ item.point }}</p>
				</div>
			</div>
		</div>
	</div>
	
	<!-- No History Items -->
	<div v-else-if="!isLoadingHistory" class="text-center mt-4">
		<div class="flex flex-col items-center gap-4">
			<div class="flex items-center justify-center">
				<img
					:src="props.noResults?.imageUrl"
					alt="No History Found"
					class="w-30 h-30"
				/>
			</div>
			<div>
				<h3 class="text-lg font-semibold mb-2">{{ props.noResults?.title }}</h3>
				<p class="text-neutral-grey-extradark">{{ props.noResults?.message }}</p>
			</div>
		</div>
	</div>
</div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SubTabHistory {
    .subtab-history-item {
        @apply bg-neutral-grey-ultralight rounded-xl p-4;
        border: 1.5px solid var(--neutral-grey-light);
        
        @media #{var.$query-max-md} {
            padding: 12px;
        }
    }

    .flight-info-grid {
        display: grid;
        grid-template-rows: auto auto auto;
        gap: 6px;
        align-items: start;
        
        @media #{var.$query-max-md} {
            gap: 4px;
        }
    }

    .date-slot {
        @apply text-sm;
        min-height: 20px;
        line-height: 1.2;
    }

    .airport-code-slot {
        display: flex;
        justify-content: flex-start;
        margin: 2px 0;
    }

    .description-slot {
        @apply text-sm font-semibold;
        min-height: auto;
        line-height: 1.3;
        word-wrap: break-word;
        
        @media #{var.$query-max-md} {
            font-size: 12px;
            line-height: 1.2;
        }
    }

    .airport-code-tag {
        color: white;
        background-color: var(--primary-blue-base);
        border-radius: 6px;
        font-weight: 700;
        line-height: 1;
        text-align: center;
        min-width: 54px;
        max-width: 54px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        @apply text-xs;
        flex-shrink: 0;
        
        @media #{var.$query-max-md} {
            min-width: 48px;
            max-width: 48px;
            height: 28px;
            font-size: 10px;
        }
    }

    @media #{var.$query-max-md} {
        .flex {
            &:first-child {
                gap: 8px;
            }
        }
    }

    .icon-arrow {
        @apply w-4 h-4;
        transition: all ease 200ms;
        transform-origin: center;
        fill: var(--primary-blue-base);
    }
}
</style>

